import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Spinner, ProgressBar, Table, Row, Col, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { uploadMultipleInvoices, getExtractionRules } from '../services/api/extractionApi';
import { getCustomers, getCustomersForDropdown } from '../services/api/customerApi';
import { getRulesetTypesByCustomer } from '../services/api/rulesetApi';
import { getMetadataFieldsByCustomer } from '../services/api/metadataApi';
import MetadataModal from './MetadataModal';
import { usePageBreadcrumbs } from '../hooks/usePageBreadcrumbs';


const InvoiceCapture = () => {
  usePageBreadcrumbs([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isZipFile, setIsZipFile] = useState(false);
  const [extractionRule, setExtractionRule] = useState('');
  const [extractionRules, setExtractionRules] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [metadataFields, setMetadataFields] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const navigate = useNavigate();
  

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (customerId) {
      loadExtractionRulesByCustomer(customerId);
      loadMetadataFields(customerId);
    } else {
      setExtractionRules([]);
      setExtractionRule('');
      setMetadataFields([]);
      setMetadata({});
    }
  }, [customerId]);

  const loadCustomers = async () => {
    try {
      const response = await getCustomersForDropdown();
      setCustomers(response.data.items || []);
      
      // Check for warning header
      const warningHeader = response.headers['x-customer-warning'];
      if (warningHeader) {
        console.warn('Customer dropdown warning:', warningHeader);
      }
    } catch (err) {
      console.error('Failed to load customers:', err);
      setCustomers([]); // Fallback to empty list
    }
  };

  const loadExtractionRulesByCustomer = async (customerId) => {
    try {
      const response = await getRulesetTypesByCustomer(customerId);
      setExtractionRules(response.data || []);
    } catch (err) {
      console.error('Failed to load extraction rules for customer:', err);
      setExtractionRules([]);
    }
  };

  const loadMetadataFields = async (customerId) => {
    try {
      const response = await getMetadataFieldsByCustomer(customerId);
      setMetadataFields(response.data || []);
    } catch (err) {
      console.error('Failed to load metadata fields:', err);
      setMetadataFields([]);
    }
  };
  
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Check if any file is a zip file
    const containsZip = selectedFiles.some(file => file.type === 'application/zip' || file.type === 'application/x-zip-compressed');
    setIsZipFile(containsZip);
    
    setFiles(selectedFiles);
    setError('');
  };

  const validateFiles = (filesToValidate) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/zip', 'application/x-zip-compressed'];
    
    for (const file of filesToValidate) {
      if (!allowedTypes.includes(file.type)) {
        setError(`File "${file.name}" is not supported. Only PDF, JPEG, PNG, and ZIP files are allowed.`);
        return false;
      }
    }
    
    return true;
  };

  const handleMetadataSave = (newMetadata) => {
    setMetadata(newMetadata);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }
    
    if (!extractionRule) {
      setError('Please select an extraction rule');
      return;
    }

    if (!customerId) {
      setError('Please select a customer');
      return;
    }

    if (!validateFiles(files)) {
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);
    
    const formData = new FormData();
    
    // Add extraction rule and customer info
    formData.append('extractionRuleId', extractionRule);
    formData.append('customerId', customerId);
    
    // Add metadata as JSON
    if (Object.keys(metadata).length > 0) {
      formData.append('Metadata', JSON.stringify(metadata));
    }
   
    // Append all files to the formData
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    try {
      // Use document ingestion API for all uploads
      const uploadResponse = await uploadMultipleInvoices(formData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });

      console.log('Upload response:', uploadResponse);
      
      if (uploadResponse && uploadResponse.data) {
        setSuccess(true);
        setUploadResults(uploadResponse.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error uploading invoices:', error);
      
      // Check if it's a validation error
      if (error.response && error.response.status === 400) {
        setError(error.response.data || 'Validation failed. Please check your metadata fields.');
      } else {
        setError('Failed to process the invoices. Please try again.');
      }
      setSuccess(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="mb-4">
        <Card className="mb-4">
          <Card.Header as="h5">Invoice Data Capture</Card.Header>
          <Card.Body>
            {!success ? (
                <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Upload Invoice(s)</Form.Label>
                  <Form.Control 
                    type="file" 
                    onChange={(e) => {
                      handleFileChange(e);
                      setError('');
                    }}
                    accept=".pdf,.jpg,.jpeg,.png,.zip"
                    multiple
                  />
                  <Form.Text className="text-muted">
                    Upload PDF or image files of your invoices for automatic data extraction. 
                    You can select multiple files or upload a ZIP file containing multiple invoices.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Customer</Form.Label>
                  <Form.Select 
                    value={customerId}
                    onChange={(e) => {
                      setCustomerId(e.target.value);
                      setError('');
                    }}
                    required
                  >
                    <option value="">Select customer...</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>{customer.customerName}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select the customer this invoice is for.
                  </Form.Text>
                </Form.Group>

                {customerId && (
                  <Form.Group className="mb-3">
                    <Form.Label>Extraction Rule</Form.Label>
                    <Form.Select 
                      value={extractionRule}
                      onChange={(e) => {
                        setExtractionRule(e.target.value);
                        setError('');
                      }}
                      required
                    >
                      <option value="">Select extraction rule...</option>
                      {extractionRules.map((rule) => (
                        <option key={rule.id} value={rule.id}>{rule.name}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Select the appropriate extraction rule for these invoices.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Add Metadata Button */}
                {customerId && extractionRule && (
                  <Form.Group className="mb-3">
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowMetadataModal(true)}
                      disabled={metadataFields.length === 0}
                    >
                      Add Metadata
                    </Button>
                    {metadataFields.length === 0 && (
                      <Form.Text className="text-muted d-block">
                        No metadata fields configured for this customer.
                      </Form.Text>
                    )}
                    {Object.keys(metadata).length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted">
                          {Object.keys(metadata).length} metadata field(s) added
                        </small>
                      </div>
                    )}
                  </Form.Group>
                )}
                
                {uploading && (
                  <div className="mb-3">
                    <ProgressBar 
                      now={uploadProgress} 
                      label={`${uploadProgress}%`} 
                      animated 
                    />
                  </div>
                )}
                
                {error && <Alert variant="danger">{error}</Alert>}
                
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={uploading || files.length === 0 || !customerId || !extractionRule}
                >
                  {uploading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Processing...
                    </>
                  ) : (
                    'Upload and Process'
                  )}
                </Button>
              </Form>
            ) : (
              <div>
                <Alert variant="success" className="mb-3">
                  Invoice(s) successfully processed!
                </Alert>
                
                <h5>Upload Results:</h5>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Status</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResults.map((result, index) => (
                      <tr key={index}>
                        <td>{result.fileName}</td>
                        <td>
                          <span className={result.status === 'Success' ? 'text-success' : 'text-danger'}>
                            {result.status}
                          </span>
                        </td>
                        <td>{result.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setSuccess(false);
                    setFiles([]);
                    setUploadResults([]);
                    setUploadProgress(0);
                  }}
                >
                  Upload More Invoices
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

       
      </div>

      {/* Metadata Modal */}
      <MetadataModal
        show={showMetadataModal}
        onHide={() => setShowMetadataModal(false)}
        metadataFields={metadataFields}
        onSave={handleMetadataSave}
        initialMetadata={metadata}
      />
    </>
  );
};

export default InvoiceCapture;